// ─────────────────────────────────────────────────────────────────────────────
// Production Logging Service - Remote Logging for Sync Issues
// Integrates with existing DataDog logging endpoint for production visibility
// ─────────────────────────────────────────────────────────────────────────────

import { ApiClient } from '../api/generated';
import networkService from '../hooks/services/networkService';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export interface Properties {
  Application?: string;
  Device?: string;
  SourceContext?: string;
  Version?: string;
  Environment?: string;
  LiteDBPath?: string;
}

export interface LogEntry {
  Timestamp: string; // ISO string format
  Level?: string;
  MessageTemplate?: string;
  RenderedMessage?: string;
  Properties?: Properties;
}

export interface SyncLogContext {
  operation: string;
  recordId?: string;
  attemptNumber?: number;
  sessionId?: string;
  apiEndpoint?: string;
  responseStatus?: number;
  errorDetails?: any;
}

class ProductionLoggingService {
  private apiClient: ApiClient | null = null;
  private sessionId: string;
  private logBuffer: LogEntry[] = [];
  private isFlushingLogs = false;
  private maxBufferSize = 50;
  private flushInterval = 30000; // 30 seconds
  private flushTimer: NodeJS.Timeout | null = null;
  private networkUnsubscribe: (() => void) | null = null;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.startPeriodicFlush();
    this.setupNetworkMonitoring();
  }

  private setupNetworkMonitoring(): void {
    // Subscribe to network status changes to retry log flushing when coming online
    this.networkUnsubscribe = networkService.onOnline(() => {
      // When we come back online, attempt to flush buffered logs
      if (this.logBuffer.length > 0) {
        this.flushLogsToRemote();
      }
    });
  }

  public setApiClient(apiClient: ApiClient): void {
    this.apiClient = apiClient;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getCurrentUserId(): string | undefined {
    try {
      const authStateJson = localStorage.getItem('UserAuthState');
      const authState = authStateJson ? JSON.parse(authStateJson) : null;
      return authState?.user?.id || authState?.userId;
    } catch {
      return undefined;
    }
  }

  private getApplicationVersion(): string {
    try {
      return process.env.REACT_APP_VERSION || '1.0.0';
    } catch {
      return '1.0.0';
    }
  }

  private createLogEntry(
    level: LogLevel, 
    messageTemplate: string, 
    renderedMessage: string,
    sourceContext: string,
    metadata?: Record<string, any>
  ): LogEntry {
    const properties: Properties = {
      Application: 'SedationApp',
      Device: navigator.userAgent,
      SourceContext: sourceContext,
      Version: this.getApplicationVersion(),
      Environment: process.env.NODE_ENV || 'unknown',
      LiteDBPath: undefined // Not applicable for web client
    };

    // Add any additional metadata to properties if needed
    if (metadata) {
      Object.keys(metadata).forEach(key => {
        (properties as any)[key] = metadata[key];
      });
    }

    return {
      Timestamp: new Date().toISOString(),
      Level: level,
      MessageTemplate: messageTemplate,
      RenderedMessage: renderedMessage,
      Properties: properties
    };
  }

  // Helper method for structured logging with templates
  public logStructured(
    level: LogLevel,
    messageTemplate: string,
    properties: Record<string, any> = {},
    sourceContext: string = 'general'
  ): void {
    // Create rendered message by replacing placeholders
    let renderedMessage = messageTemplate;
    Object.keys(properties).forEach(key => {
      const placeholder = `{${key}}`;
      if (renderedMessage.includes(placeholder)) {
        renderedMessage = renderedMessage.replace(
          new RegExp(`\\{${key}\\}`, 'g'), 
          String(properties[key] || '')
        );
      }
    });

    this.log(level, messageTemplate, renderedMessage, sourceContext, properties);
  }

  // Core logging methods
  public debug(message: string, metadata?: Record<string, any>): void {
    this.log('debug', message, message, 'general', metadata);
  }

  public info(message: string, metadata?: Record<string, any>): void {
    this.log('info', message, message, 'general', metadata);
  }

  public warn(message: string, metadata?: Record<string, any>): void {
    this.log('warn', message, message, 'general', metadata);
  }

  public error(message: string, metadata?: Record<string, any>): void {
    this.log('error', message, message, 'general', metadata);
  }

  public critical(message: string, metadata?: Record<string, any>): void {
    this.log('critical', message, message, 'general', metadata);
  }

  // Specialized sync logging methods
  public logSyncStart(context: Partial<SyncLogContext> = {}): void {
    const messageTemplate = "Sync operation started for {Operation}";
    const renderedMessage = `Sync operation started for ${context.operation || 'unknown'}`;
    this.log('info', messageTemplate, renderedMessage, 'sync', {
      Operation: context.operation || 'unknown',
      RecordId: context.recordId,
      SessionId: context.sessionId || this.sessionId,
      Timestamp: new Date().toISOString()
    });
  }

  public logSyncSuccess(context: Partial<SyncLogContext> = {}): void {
    const messageTemplate = "Sync operation {Operation} completed successfully";
    const renderedMessage = `Sync operation ${context.operation || 'unknown'} completed successfully`;
    this.log('info', messageTemplate, renderedMessage, 'sync', {
      Operation: context.operation || 'unknown',
      RecordId: context.recordId,
      AttemptNumber: context.attemptNumber,
      ApiEndpoint: context.apiEndpoint,
      ResponseStatus: context.responseStatus
    });
  }

  public logSyncError(error: Error | any, context: Partial<SyncLogContext> = {}): void {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const messageTemplate = "Sync operation {Operation} failed: {ErrorMessage}";
    const renderedMessage = `Sync operation ${context.operation || 'unknown'} failed: ${errorMessage}`;
    this.log('error', messageTemplate, renderedMessage, 'sync', {
      Operation: context.operation || 'unknown',
      RecordId: context.recordId,
      AttemptNumber: context.attemptNumber,
      ApiEndpoint: context.apiEndpoint,
      ResponseStatus: context.responseStatus,
      ErrorName: error instanceof Error ? error.name : 'Unknown',
      ErrorMessage: errorMessage,
      ErrorStack: error instanceof Error ? error.stack : undefined,
      IsAxiosError: error && typeof error === 'object' && 'isAxiosError' in error,
      ResponseData: error && typeof error === 'object' && 'response' in error ? error.response?.data : undefined,
      RequestUrl: error && typeof error === 'object' && 'config' in error ? error.config?.url : undefined,
      RequestMethod: error && typeof error === 'object' && 'config' in error ? error.config?.method : undefined
    });
  }

  public logAuthError(error: Error | any, context: Record<string, any> = {}): void {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const messageTemplate = "Authentication error: {ErrorMessage}";
    const renderedMessage = `Authentication error: ${errorMessage}`;
    this.log('error', messageTemplate, renderedMessage, 'auth', {
      ErrorType: error instanceof Error ? error.name : 'Unknown',
      ErrorMessage: errorMessage,
      HasToken: context.hasToken,
      TokenExpiry: context.tokenExpiry,
      IsTokenExpired: context.isTokenExpired,
      CurrentUrl: window.location.href
    });
  }

  public logNetworkError(error: Error | any, context: Record<string, any> = {}): void {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const messageTemplate = "Network error: {ErrorMessage}";
    const renderedMessage = `Network error: ${errorMessage}`;
    this.log('error', messageTemplate, renderedMessage, 'network', {
      ErrorMessage: errorMessage,
      IsOnline: navigator.onLine,
      ApiUrl: process.env.REACT_APP_API_URL,
      Endpoint: context.endpoint,
      Method: context.method,
      ResponseStatus: context.responseStatus
    });
  }

  // Core logging method
  private log(level: LogLevel, messageTemplate: string, renderedMessage: string, sourceContext: string, metadata?: Record<string, any>): void {
    const logEntry = this.createLogEntry(level, messageTemplate, renderedMessage, sourceContext, metadata);
    
    // Always log to console for development
    if (process.env.NODE_ENV === 'development') {
      const consoleMethod = level === 'debug' ? 'log' : level === 'critical' ? 'error' : level;
      console[consoleMethod](`[${sourceContext.toUpperCase()}] ${renderedMessage}`, metadata);
    }

    // Add to buffer for remote logging
    this.logBuffer.push(logEntry);

    // Flush immediately for critical errors (will only send if online)
    if (level === 'critical' || level === 'error') {
      this.flushLogsToRemote();
    }

    // Flush if buffer is full (will only send if online)
    if (this.logBuffer.length >= this.maxBufferSize) {
      this.flushLogsToRemote();
    }

    // Emit event for debug modal real-time updates
    this.emitLogEvent(logEntry);
  }

  // Emit log events for real-time debug modal updates
  private emitLogEvent(logEntry: LogEntry): void {
    try {
      const event = new CustomEvent('production-log', { 
        detail: logEntry 
      });
      window.dispatchEvent(event);
    } catch (error) {
      // Silent fail - don't break logging if event dispatch fails
    }
  }

  // Flush logs to remote endpoint
  private async flushLogsToRemote(): Promise<void> {
    if (this.isFlushingLogs || this.logBuffer.length === 0 || !this.apiClient) {
      return;
    }

    // Skip remote logging when offline - logs will remain in buffer for retry when online
    if (!networkService.isOnline()) {
      return;
    }

    this.isFlushingLogs = true;
    const logsToFlush = [...this.logBuffer];
    this.logBuffer = []; // Clear buffer immediately

    try {
      // Send logs in the format expected by the API
      // The API expects LogInfo to be either LogEntry[] or a single LogEntry
      await this.apiClient.log.postApiLogAddLog({
        LogInfo: logsToFlush // Send as LogEntry[] array
      });

    } catch (error) {
      console.error('Failed to send logs to remote endpoint:', error);
      
      // Put logs back in buffer to retry later (keep only most recent)
      this.logBuffer = [...logsToFlush.slice(-25), ...this.logBuffer].slice(-this.maxBufferSize);
    } finally {
      this.isFlushingLogs = false;
    }
  }

  // Start periodic flush
  private startPeriodicFlush(): void {
    this.flushTimer = setInterval(() => {
      this.flushLogsToRemote();
    }, this.flushInterval);
  }

  // Public method to force flush logs
  public async flushNow(): Promise<void> {
    return this.flushLogsToRemote();
  }

  // Get logs for debug modal (recent logs)
  public getRecentLogs(): LogEntry[] {
    return [...this.logBuffer].reverse(); // Most recent first
  }

  // Cleanup
  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    
    // Clean up network subscription
    if (this.networkUnsubscribe) {
      this.networkUnsubscribe();
      this.networkUnsubscribe = null;
    }
    
    // Final flush (will only send if online)
    this.flushLogsToRemote();
  }
}

// Singleton instance
export const productionLogger = new ProductionLoggingService();

// Export convenience methods
export const logSyncError = (error: Error | any, context: Partial<SyncLogContext> = {}) => 
  productionLogger.logSyncError(error, context);

export const logSyncSuccess = (context: Partial<SyncLogContext> = {}) => 
  productionLogger.logSyncSuccess(context);

export const logSyncStart = (context: Partial<SyncLogContext> = {}) => 
  productionLogger.logSyncStart(context);

export const logAuthError = (error: Error | any, context: Record<string, any> = {}) =>
  productionLogger.logAuthError(error, context);

export const logNetworkError = (error: Error | any, context: Record<string, any> = {}) =>
  productionLogger.logNetworkError(error, context);

// Export convenience method for structured logging
export const logStructured = (
  level: LogLevel,
  messageTemplate: string,
  properties: Record<string, any> = {},
  sourceContext: string = 'general'
) => productionLogger.logStructured(level, messageTemplate, properties, sourceContext);
